// Functie als CANONIEKE waarde (eigenaarsbesluit 5 augustus 2026).
//
// Het veld Functie was vrije tekst en daardoor onbruikbaar om op te filteren of te
// matchen: 29.724 gevulde functies leverden duizenden varianten op, met de titel
// vaak verstopt achter een rij skills ('Full Stack Developer | Python | SQL |
// JavaScript'). Vanaf nu kiest een gebruiker uit functie_registry en kan er niets
// nieuws ontstaan zonder dat het een echte registerregel wordt.
//
// Dit bestand is BEWUST puur: geen DB, geen React, zodat `node --test` elke rand
// kan raken (zie functie.test.mjs) en de normalisatie op precies EEN plek staat.
// De seed in lib/db.js gebruikt dezelfde regel in SQL, de API-route en beide
// invoervelden gebruiken deze functie.
//
// EEN FEIT, EEN BRON (eenduidige functietitel, 4 oktober 2026). De IDENTITEIT van
// een opgeslagen titel is functieIdentity (JS) en functieIdentitySql (SQL), allebei
// hier en nergens anders; lib/one-fact-one-source.test.mjs bewaakt dat. De identiteit
// knipt niets af, kapt niets af, vouwt geen accenten en verandert geen casing van de
// weergave: 'Coördinator' en 'Coordinator' zijn twee titels, 'QA Manager / Test
// Automation Engineer' is EEN titel, 'D - FL - Java developer' ook.

/**
 * De scheidingstekens die een titel van zijn staart scheiden: een pijp, een schuine
 * streep, of een koppelteken MET spaties eromheen. Een koppelteken zonder spaties
 * hoort bij het woord ('Front-end Developer') en knipt dus niets af.
 */
const SEPARATOR = /\||\/|\s-\s/;

/** Langer dan dit is geen functietitel meer maar een zin. */
export const MAX_FUNCTIE_LEN = 80;

/** Hoeveel suggesties het invoerveld toont. */
export const FUNCTIE_SUGGEST_LIMIT = 8;

/**
 * Vanaf welk gebruik een titel zijn volle populariteitsbonus krijgt. Boven deze
 * grens telt extra gebruik niet mee, anders zou 'DevOps Engineer' (947x) elke
 * betere tekstmatch wegdrukken.
 */
export const FUNCTIE_USAGE_CAP = 500;

/** Hoe zwaar populariteit meeweegt naast de tekstgelijkenis (0 tot 1). */
export const FUNCTIE_USAGE_WEIGHT = 0.2;

/**
 * De kop van een getypte of geimporteerde functietekst.
 *
 * Regel: neem alles VOOR het eerste scheidingsteken, haal witruimte weg aan de
 * randen, en pers herhaalde witruimte samen tot een spatie.
 *   'Full Stack Developer | Python | SQL'  -> 'Full Stack Developer'
 *   'Sales / Account Manager'              -> 'Sales'
 *   'Data Engineer - Azure'                -> 'Data Engineer'
 *   'Front-end   Developer'                -> 'Front-end Developer'
 *
 * @param {unknown} raw
 * @returns {string} de genormaliseerde titel, of '' als er niets overblijft
 */
export function normalizeFunctieInput(raw) {
  let s = String(raw == null ? '' : raw);
  const m = SEPARATOR.exec(s);
  if (m) s = s.slice(0, m.index);
  s = s.trim().replace(/\s+/g, ' ');
  return s.length > MAX_FUNCTIE_LEN ? s.slice(0, MAX_FUNCTIE_LEN).trim() : s;
}

/**
 * De sleutel van GETYPTE invoer: de kop (normalizeFunctieInput) in de identiteit
 * (functieIdentity). Casing is presentatie, geen identiteit: 'DevOps Engineer' en
 * 'devops engineer' zijn dezelfde functie. Voor de suggestie-query en de voorvertoning
 * van '+ Voeg toe'; NIET de opgeslagen identiteit van een samengestelde titel of een
 * jobnaam, die is functieIdentity van de hele tekst.
 *
 * @param {unknown} raw
 * @returns {string}
 */
export function functieKey(raw) {
  return functieIdentity(normalizeFunctieInput(raw));
}

/**
 * Elke witruimte die JavaScript onder \s verstaat, letterlijk uitgeschreven: ASCII
 * plus NBSP (U+00A0), U+1680, U+2000 tot en met U+200A, U+2028, U+2029, smalle NBSP
 * (U+202F), U+205F, U+3000 en de BOM (U+FEFF). Waarom niet alleen \s plus NBSP:
 * Postgres verstaat onder \s bij een C-collatie alleen ASCII, en bij een libc-collatie
 * wat glibc spatie noemt (zonder NBSP, smalle NBSP en BOM). Zonder deze lijst zouden JS
 * en SQL een geplakte LinkedIn-titel met een smalle NBSP verschillend opslaan.
 *
 * In de JS-bron staat de backslash dubbel ('\\s'), zodat de SQL-tekst EEN backslash
 * draagt; de \u-escapes zijn in de string al letterlijke tekens. JS en SQL gebruiken
 * deze ene string, dus ze kunnen niet uit elkaar lopen.
 */
// \u0085 (NEL) staat erbij omdat Postgres \s onder C.UTF-8 die ook als witruimte ziet en JS niet (review U1).
const WS_CLASS = '[\\s\u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff]+';
const WS_RE = new RegExp(WS_CLASS, 'g');

/**
 * Wat de matchsleutel laat staan: kleine letters, cijfers, #, +, . en elke letter uit
 * Latin-1 Supplement en Latin Extended (U+00C0 tot en met U+024F, met de twee
 * rekentekens daartussen, die in een functietitel niet voorkomen). Zo blijven
 * 'coördinator' en 'coordinator' twee sleutels, 'c#', 'c++' en 'c' drie, '.net' en 'net'
 * twee. Alleen spaties, koppeltekens, schuine strepen, haakjes en andere leestekens
 * vallen weg. Dezelfde string in JS en SQL, net als WS_CLASS.
 */
const MATCH_DROP_CLASS = '[^a-z0-9#+.\u00c0-\u024f]+';
const MATCH_DROP_RE = new RegExp(MATCH_DROP_CLASS, 'g');

/**
 * Witruimte rechtzetten zonder iets anders te raken: elke reeks witruimte (ook NBSP,
 * smalle NBSP, BOM, tab, regelafbreking) wordt een spatie, de randen gaan eraf. Geen
 * knip, geen casing. De eerste stap van canonicalTitle, zodat zulke tekens nooit in
 * het register of een override belanden.
 *
 * @param {unknown} raw
 * @returns {string}
 */
export function functieWhitespace(raw) {
  return String(raw ?? '').replace(WS_RE, ' ').trim();
}

/**
 * DE identiteit van een functietitel (een feit, een bron). functie_registry.name_lower
 * is functieIdentity(name). Geen knip bij een scheidingsteken, geen lengtegrens, geen
 * accentvouwing: alleen witruimte en kleine letters.
 *
 * @param {unknown} s
 * @returns {string}
 */
export function functieIdentity(s) {
  return functieWhitespace(s).toLowerCase();
}

/**
 * De enige SQL-spelling van functieIdentity. `col` is een kolom of expressie van de
 * aanroeper, nooit gebruikerstekst. Volgorde gelijk aan JS: witruimte samenpersen,
 * randen eraf (na het samenpersen is elke rand een gewone spatie, dus btrim volstaat),
 * kleine letters.
 *
 * @param {string} col
 * @returns {string}
 */
export function functieIdentitySql(col) {
  return `lower(btrim(regexp_replace(${col}, '${WS_CLASS}', ' ', 'g')))`;
}

/**
 * De VERGELIJKINGSSLEUTEL over spelling heen: de identiteit zonder spaties en
 * leestekens. 'Enterprise Architect', 'Enterprise-architect' en 'Enterprisearchitect'
 * zijn dezelfde functie; welke weergavevorm wint bepaalt het register (audit
 * Architectuur 12 augustus 2026: 149 kandidaten verdeeld over drie spellingen van
 * dezelfde titel). Alleen voor de resolver, de registerschrijver en de
 * samenvoegtool, nooit als filter.
 *
 * UITZONDERING: #, + en . blijven staan, en letters met een accent ook. Zonder die
 * uitzondering pletten 'C# Developer', 'C++ Developer' en 'C Developer' tot dezelfde
 * sleutel, '.NET Developer' en 'NET Developer' ook, en verloor 'Coördinator' zijn ö
 * (de sleutel 'cordinator' vond 'Coordinator' nooit, en 'Coördinator' ook niet).
 *
 * Knipt NIETS af: de aanroeper geeft al genormaliseerde tekst mee. Getypte invoer
 * gaat eerst door functieKey (de kop voor het eerste scheidingsteken), een jobnaam
 * gaat heel door.
 *
 * @param {unknown} raw
 * @returns {string}
 */
export function functieMatchKey(raw) {
  return functieIdentity(raw).replace(MATCH_DROP_RE, '');
}

/**
 * De enige SQL-spelling van functieMatchKey, gebouwd op functieIdentitySql. De
 * registeropzoeking (lib/crm/functie-resolve.mjs), de schrijver
 * (lib/crm/functie-registry-write.mjs) en de index idx_functie_registry_match
 * (lib/db.js) roepen dit aan met 'name_lower'; een letterlijk gelijke expressie is
 * wat de index bruikbaar maakt.
 *
 * @param {string} col
 * @returns {string}
 */
export function functieMatchKeySql(col) {
  return `regexp_replace(${functieIdentitySql(col)}, '${MATCH_DROP_CLASS}', '', 'g')`;
}

/**
 * Letterlijk maken wat in LIKE een joker is. Zonder dit matcht een getypte
 * underscore of procent half het register.
 *
 * @param {unknown} s
 * @returns {string}
 */
export function likeEscape(s) {
  return String(s == null ? '' : s).replace(/[\\%_]/g, (m) => `\\${m}`);
}

/**
 * De rangschikking van de suggesties, als SQL-expressie.
 *
 * Waarom niet puur similarity(): pg_trgm scoort op tekenreeksen, dus wie 'Full'
 * typt haalt 'Full Stack Developer' (0,2) niet boven de drempel terwijl dat exact
 * de bedoeling is. Vandaar een trap: gelijk > begint met > bevat > trigram, en pas
 * daarna een afgetopte populariteitsbonus als tiebreak.
 *
 * De parameters zijn $-plaatsvervangers van de aanroeper (nooit gebruikerstekst),
 * dus deze samenstelling is injectievrij.
 *
 * @param {{q?: string, prefix?: string, contains?: string, usageCap?: number, usageWeight?: number}} [p]
 * @returns {string} SQL-expressie die een score tussen 0 en ~1,2 oplevert
 */
export function functieScoreExpr(p = {}) {
  const q = p.q || '$1';
  const prefix = p.prefix || '$2';
  const contains = p.contains || '$3';
  const cap = Number.isFinite(p.usageCap) ? Number(p.usageCap) : FUNCTIE_USAGE_CAP;
  const weight = Number.isFinite(p.usageWeight) ? Number(p.usageWeight) : FUNCTIE_USAGE_WEIGHT;
  return `(GREATEST(
      similarity(name_lower, ${q}),
      CASE WHEN name_lower = ${q} THEN 1.0
           WHEN name_lower LIKE ${prefix} THEN 0.9
           WHEN name_lower LIKE ${contains} THEN 0.7
           ELSE 0 END
    ) + LEAST(usage_count, ${cap})::float / ${cap} * ${weight})`;
}
